import { useEffect } from "react";
import { COMPANY } from "@/lib/content";

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Lightweight SEO component (static-friendly, no SSR).
 * Manages title, meta description, Open Graph, Twitter cards, canonical & JSON-LD.
 */
export const SEO = ({ title, description, path = "/", image, schema }) => {
  const fullTitle = title ? `${title} | ${COMPANY.name}` : `${COMPANY.name} — ${COMPANY.tagline}`;
  const canonical = `${COMPANY.url}${path}`;
  const ogImage = image || `${COMPANY.url}/og-image.svg`;

  useEffect(() => {
    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:site_name", COMPANY.name);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);
    setLink("canonical", canonical);

    const id = "jsonld-schema";
    let script = document.getElementById(id);
    const data = schema || {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: COMPANY.name,
      url: COMPANY.url,
      description: COMPANY.tagline,
      email: COMPANY.email,
    };
    if (!script) {
      script = document.createElement("script");
      script.id = id;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }, [fullTitle, description, canonical, ogImage, schema]);

  return null;
};

export default SEO;
